<template>
  <div class="container page recharge-page" style="background-color: #1f1c1f;">
    <van-nav-bar :title="$t('ui.recharge.title')" class="nav-bar">
      <template #left>
        <img src="/img/newLogin/download.png" @click="back()" style="width: 20px;height: 15px;"/>
      </template>
    </van-nav-bar>

    <section class="balance-card">
      <p class="balance-label">{{ $t('ui.recharge.current_balance') }}</p>
      <p class="balance-value">{{ balance }}</p>
    </section>

    <section class="form-card">
      <div class="section-block">
        <div class="block-title">{{ $t('ui.recharge.select_network') }}</div>
        <div class="network-options">
          <button
            v-for="item in networkOptions"
            :key="item.key"
            type="button"
            class="network-pill"
            :class="{ active: network === item.key }"
            @click="selectNetwork(item.key)"
          >
            {{ item.label }}
          </button>
          <button
            type="button"
            class="network-pill bank-pill"
            @click="openBankSupport"
          >
            {{ $t('ui.recharge.bank') }}
          </button>
        </div>
      </div>

      <div class="section-block">
        <div class="block-title">{{ selectedAddressTitle }}</div>
        <div class="address-box">
          <div class="address-text">{{ rechargeAddress || missingAddressText }}</div>
          <button type="button" class="copy-btn" @click="copyAddress">{{ $t('ui.common.copy') }}</button>
        </div>
      </div>

      <div class="section-block">
        <div class="block-title">{{ $t('ui.recharge.amount') }}</div>
        <van-field
          v-model="money"
          type="number"
          :label="selectedAssetLabel"
          :placeholder="$t('ui.recharge.amount_placeholder')"
          class="money-field"
        />
      </div>

      <div class="section-block">
        <div class="block-title">{{ $t('ui.recharge.upload_voucher') }}</div>
        <van-uploader
          v-model="fileList"
          :max-count="1"
          :after-read="afterRead"
          :deletable="!uploading"
        />
        <p class="upload-tip">{{ $t('ui.recharge.upload_tip', { asset: selectedAssetLabel, network: selectedNetworkLabel }) }}</p>
      </div>

      <div class="section-block notice-box">
        <p>{{ $t('ui.recharge.notice_1', { asset: selectedAssetLabel }) }}</p>
        <p>{{ $t('ui.recharge.notice_2', { network: selectedNetworkLabel }) }}</p>
        <p>{{ $t('ui.recharge.notice_3') }}</p>
      </div>

      <van-button
        block
        round
        class="submit-btn"
        :loading="submitting"
        @click="submitRecharge"
      >
        {{ $t('ui.recharge.submit') }}
      </van-button>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import { Base64 } from "js-base64";

const RECHARGE_OPTIONS = [
  { key: "USDT_ERC20", asset: "USDT", network: "ERC20", label: "USDT-ERC20" },
  { key: "USDT_TRC20", asset: "USDT", network: "TRC20", label: "USDT-TRC20" },
  { key: "BTC", asset: "BTC", network: "BTC", label: "BTC" },
  { key: "ETH", asset: "ETH", network: "ETH", label: "ETH" },
  { key: "USDC_ERC20", asset: "USDC", network: "ERC20", label: "USDC-ERC20" },
];

export default {
  computed: {
    selectedOption() {
      return RECHARGE_OPTIONS.find((item) => item.key === this.network) || RECHARGE_OPTIONS[0];
    },
    selectedAssetLabel() {
      return this.selectedOption.asset;
    },
    selectedNetworkLabel() {
      return this.selectedOption.network;
    },
    selectedAddressTitle() {
      return this.$t('ui.recharge.address_title', { asset: this.selectedAssetLabel });
    },
    missingAddressText() {
      return this.$t('ui.recharge.missing_address', { asset: this.selectedAssetLabel });
    },
  },
  data() {
    return {
      balance: 0,
      money: "",
      network: "USDT_ERC20",
      rechargeAddress: "",
      networkOptions: RECHARGE_OPTIONS,
      rechargeAddressMap: {
        USDT_ERC20: "",
        USDT_TRC20: "",
        BTC: "",
        ETH: "",
        USDC_ERC20: "",
      },
      fileList: [],
      voucherImg: "",
      uploading: false,
      submitting: false,
    };
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$router.push({ path: "/Login" });
      return;
    }
    this.getUserInfo();
    this.getBaseInfo();
  },
  methods: {
    back() {
      window.history.back();
    },
    selectNetwork(key) {
      this.network = key;
      this.updateRechargeAddress();
    },
    async ensureBaseInfo() {
      const current = this.$store.state.baseInfo || {};
      if (current && Object.keys(current).length) {
        return current;
      }
      try {
        const res = await this.$http({
          method: 'get',
          url: 'base_info'
        });
        if (res && res.data) {
          this.$store.commit('setBaseInfoValue', res.data);
          return res.data;
        }
      } catch (error) {
        return current;
      }
      return current;
    },
    async openBankSupport() {
      const service = await this.ensureBaseInfo();
      if (service.iskefu == 1 && service.kefu) {
        window.location.href = service.kefu;
        return;
      }
      this.$router.push('/ServiceOnline');
    },
    getUserInfo() {
      this.$http({
        method: "get",
        url: "user_info",
      }).then((res) => {
        if (res.code === 200) {
          this.balance = res.data.money || 0;
        }
      });
    },
    getBaseInfo() {
      this.$http({
        method: "get",
        url: "base_info",
      }).then((res) => {
        if (res.code === 200) {
          const usdtAddress = res.data.usdt_recharge_address || "";
          this.rechargeAddressMap = {
            USDT_ERC20: usdtAddress,
            USDT_TRC20: usdtAddress,
            BTC: res.data.btc_recharge_address || "",
            ETH: res.data.eth_recharge_address || "",
            USDC_ERC20: res.data.usdc_recharge_address || "",
          };
          this.updateRechargeAddress();
        }
      });
    },
    updateRechargeAddress() {
      this.rechargeAddress = this.rechargeAddressMap[this.network] || "";
    },
    copyAddress() {
      if (!this.rechargeAddress) {
        this.$toast(this.missingAddressText);
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.rechargeAddress).then(() => {
          this.$toast(this.$t('ui.recharge.toast_copied'));
        });
        return;
      }
      const input = document.createElement("input");
      input.value = this.rechargeAddress;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      this.$toast(this.$t('ui.recharge.toast_copied'));
    },
    async afterRead(file) {
      this.uploading = true;
      file.status = "uploading";
      file.message = this.$t('ui.recharge.uploading');
      try {
        const formData = new FormData();
        formData.append("file", file.file);
        const response = await axios.post(
          "https://admin699.ltalk6.com/api/member/uploadRechargeVoucher",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              token: Base64.encode(localStorage.getItem("token") || ""),
              lang: "zh",
            },
            withCredentials: false,
          }
        );
        const result = response.data || {};
        const payload = result.data ? JSON.parse(Base64.decode(result.data)) : {};
        if (result.code !== 200 || !payload.url) {
          throw new Error(result.msg || this.$t('ui.recharge.upload_failed'));
        }
        this.voucherImg = payload.url;
        file.status = "done";
        file.message = this.$t('ui.recharge.uploaded');
      } catch (error) {
        this.fileList = [];
        this.voucherImg = "";
        this.$toast(error.message || this.$t('ui.recharge.upload_failed'));
      } finally {
        this.uploading = false;
      }
    },
    submitRecharge() {
      const amount = Number(this.money);
      if (!this.rechargeAddress) {
        this.$toast(this.missingAddressText);
        return;
      }
      if (!amount || amount <= 0) {
        this.$toast(this.$t('ui.recharge.amount_placeholder'));
        return;
      }
      if (!this.voucherImg) {
        this.$toast(this.$t('ui.recharge.toast_need_voucher'));
        return;
      }

      this.submitting = true;
      this.$http({
        method: "post",
        data: {
          money: amount,
          network: this.network,
          voucher_img: this.voucherImg,
        },
        url: "recharge",
      }).then((res) => {
        if (res.code === 200) {
          this.$toast(res.msg || this.$t('ui.recharge.toast_submit_ok'));
          this.money = "";
          this.voucherImg = "";
          this.fileList = [];
        } else {
          this.$toast(res.msg || this.$t('ui.recharge.toast_submit_fail'));
        }
      }).finally(() => {
        this.submitting = false;
      });
    },
  },
  watch: {
    network() {
      this.updateRechargeAddress();
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/css/base.css";

.recharge-page {
  min-height: 100vh;
}

.balance-card,
.form-card {
  margin: 24px;
  padding: 28px;
  border: 1px solid #766350;
  border-radius: 28px;
  background: linear-gradient(180deg, #212121, #2f241e);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
}

.balance-label,
.block-title,
.upload-tip,
.notice-box p,
.address-text {
  color: #f3c7a2;
}

.balance-label {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.balance-value {
  margin: 16px 0 0;
  color: #fff;
  font-size: 54px;
  font-weight: 800;
}

.section-block + .section-block {
  margin-top: 28px;
}

.block-title {
  margin-bottom: 16px;
  font-size: 26px;
  font-weight: 700;
}

.network-options {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.network-pill {
  min-width: 150px;
  padding: 16px 24px;
  border: 1px solid #766350;
  border-radius: 999px;
  background: rgba(17, 18, 21, 0.72);
  color: #f3c7a2;
  font-size: 24px;
  font-weight: 700;
}

.network-pill.active {
  border-color: #f0be88;
  background: linear-gradient(180deg, #6a4730, #33251e);
}

.address-box {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid #766350;
  border-radius: 18px;
  background: rgba(17, 18, 21, 0.58);
}

.address-text {
  flex: 1;
  word-break: break-all;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.6;
}

.copy-btn {
  padding: 14px 22px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #f0be88, #b27d4e);
  color: #241913;
  font-size: 22px;
  font-weight: 800;
}

::v-deep .money-field {
  border: 1px solid #766350;
  border-radius: 18px;
  background: rgba(17, 18, 21, 0.58);
}

::v-deep .money-field .van-cell {
  background: transparent;
}

::v-deep .money-field .van-field__label,
::v-deep .money-field input {
  color: #f3c7a2;
  font-size: 24px;
}

::v-deep .money-field input::placeholder {
  color: rgba(243, 199, 162, 0.48);
}

::v-deep .van-uploader__upload {
  width: 180px;
  height: 180px;
  border-radius: 20px;
  background: rgba(17, 18, 21, 0.58);
  border: 1px dashed #766350;
}

::v-deep .van-uploader__preview {
  margin: 0 18px 18px 0;
}

::v-deep .van-uploader__preview-image,
::v-deep .van-uploader__file {
  width: 180px;
  height: 180px;
  border-radius: 20px;
}

.upload-tip {
  margin: 14px 0 0;
  font-size: 20px;
  line-height: 1.6;
}

.notice-box {
  padding: 20px;
  border: 1px solid rgba(240, 190, 136, 0.24);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
}

.notice-box p {
  margin: 0;
  font-size: 20px;
  line-height: 1.8;
}

.submit-btn {
  height: 92px;
  margin-top: 32px;
  border: none;
  background: linear-gradient(180deg, #f0be88, #a86f43);
}

::v-deep .submit-btn .van-button__text {
  color: #241913;
  font-size: 28px;
  font-weight: 800;
}
</style>
